import { Grid, Typography } from '@material-ui/core';
import { motivoTransferenciaOptions, transferenciaTipoOptions } from 'common/genericOptions';
import {
  RegistroStatus,
  FuncaoParticipante,
  InvestidorResponse,
  ParticipanteResponse,
  TransferenciaTipo,
  InstrumentoFinanceiroApiResponse,
} from 'common/genericTypes';
import FinishedSign from 'components/finishedSign';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { Button, Dropdown, LgSpace, TextField, Section, Option, NumberField, SmSpace, AutoComplete } from 'm2m.components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addAlert } from 'store/modules/snackBar';
import { RootState } from 'store';
import { addCpfCnpjMask } from 'utils/mask';
import { formatNumber } from 'utils/number';
import { useGenericStyles } from 'common/genericStyles';
import { Cancel } from '@material-ui/icons';
import InstrumentoAutocomplete from 'components/operacoes/instrumentoAutocomplete';
import { getQuantityType } from 'utils/instrumentoFinanceiroUtils';
import { POSTTransferenciaCustodiaNova, POSTTransferenciaTitularidadeNova, POSTTransferenciaLoteComprovantes, GETInstrumentosFinanceirosTransferencia, InstrumentoFinanceiroResumoTransferencia, GETInstrumentoInadimplencia } from 'requests/operacao';

const CadastrarOperacaoTransferencia: React.FC = () => {
  const dispatch = useDispatch();
  const classes = useGenericStyles();
  const getParticipantes = useSelector((state: RootState) => state.participanteApi.getParticipantesResume);
  const getInvestidores = useSelector((state: RootState) => state.investidorApi.getInvestidores);
  const getPosicoes = useSelector((state: RootState) => state.investidorApi.getPosicoes);

  const [instrumentos, setInstrumentos] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);
  const [instrumentoSearchLoading, setInstrumentoSearchLoading] = useState<boolean>(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [investidorOrigemOptions, setInvestidorOrigemOptions] = useState<Array<Option>>([]);
  const [investidorDestinoOptions, setInvestidorDestinoOptions] = useState<Array<Option>>([]);
  const [custodianteOrigemOptions, setCustodianteOrigemOptions] = useState<Array<Option>>([]);
  const [custodianteDestinoOptions, setCustodianteDestinoOptions] = useState<Array<Option>>([]);
  const [custodiantes, setCustodiantes] = useState<Array<ParticipanteResponse>>([]);
  const [investidoresOrigem, setInvestidoresOrigem] = useState<Array<InvestidorResponse>>([]);
  const [investidoresDestino, setInvestidoresDestino] = useState<Array<InvestidorResponse>>([]);
  const [quantidadeMax, setQuantidadeMax] = useState<number>(0);

  const [tipo, setTipo] = useState<string>('');
  const [instrumento, setInstrumento] = useState<InstrumentoFinanceiroApiResponse | null>(null);
  const [instrumentoInadimplido, setInstrumentoInadimplido] = useState<boolean>(false);
  const [custodianteOrigemId, setCustodianteOrigemId] = useState<string>('');
  const [investidorOrigemId, setInvestidorOrigemId] = useState<string>('');
  const [contaDepositoOrigem, setContaDepositoOrigem] = useState<string>('');
  const [custodianteDestinoId, setCustodianteDestinoId] = useState<string>('');
  const [investidorDestinoId, setInvestidorDestinoId] = useState<string>('');
  const [contaDepositoDestino, setContaDepositoDestino] = useState<string>('');
  const [quantidade, setQuantidade] = useState<number>(0);
  const [motivo, setMotivo] = useState<string>('');
  const [justificativa, setJustificativa] = useState<string>('');
  const [attachments, setAttachments] = useState<Array<Attachment>>([]);

  const [finished, setFinished] = useState(false);
  const [instrumentoPage, setInstrumentoPage] = useState<number>(1);
  const [instrumentoTotalCount, setInstrumentoTotalCount] = useState<number>(0);
  const [instrumentoSearch, setInstrumentoSearch] = useState<string>('');

  const searchInstrumentos = useCallback(async (search: string, page: number = 1, append: boolean = false) => {
    try {
      setInstrumentoSearchLoading(true);
      const result = await GETInstrumentosFinanceirosTransferencia(dispatch, search, page, 20, true);
      const adapted = result.result.map((item) => ({
        instrumentoFinanceiroGuid: item.instrumentoFinanceiroGuid,
        codigo: item.codigo,
        razaoSocialEmissor: item.razaoSocialEmissor,
        numeroEmissao: item.numeroEmissao,
        tipoInstrumentoFinanceiro: item.tipoInstrumentoFinanceiro,
        criteriosRemuneracao: {
          dataEmissao: item.dataEmissao,
          volumeEmissao: item.volumeEmissao,
        },
      })) as unknown as Array<InstrumentoFinanceiroApiResponse>;
      setInstrumentos((prev) => append ? [...prev, ...adapted] : adapted);
      setInstrumentoTotalCount(result.totalCount);
      setInstrumentoPage(page);
      setInstrumentoSearch(search);
    } catch {} finally {
      setInstrumentoSearchLoading(false);
    }
  }, [dispatch]);

  const handleInstrumentoInputChange = useCallback((inputValue: string) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    // Não buscar se o valor digitado é exatamente o código do instrumento selecionado
    if (instrumento && inputValue === instrumento.codigo) return;
    debounceTimerRef.current = setTimeout(() => {
      searchInstrumentos(inputValue, 1, false);
    }, 300);
  }, [searchInstrumentos, instrumento]);

  const handleCarregarMaisInstrumentos = useCallback(() => {
    searchInstrumentos(instrumentoSearch, instrumentoPage + 1, true);
  }, [searchInstrumentos, instrumentoSearch, instrumentoPage]);

  const hasMoreInstrumentos = instrumentos.length < instrumentoTotalCount;

  // Carregar primeiros 20 ao montar (para exibir opções iniciais)
  const getInstrumentos = useCallback(async () => {
    await searchInstrumentos('', 1, false);
  }, [searchInstrumentos]);

  const getCustodiantes = useCallback(async () => {
    try {
      const result = await getParticipantes(
        dispatch,
        { funcao: FuncaoParticipante.Custodiante, status: RegistroStatus.Aprovado, suspenso: false },
        true
      );
      setCustodiantes(result);
    } catch {}
  }, [dispatch, getParticipantes]);

  const getInvestidoresOrigem = useCallback(
    async (custodianteId: string) => {
      try {
        const result = await getInvestidores(dispatch, { participanteId: custodianteId }, true);
        const options = result.map((posicao) => {
          return {
            value: posicao.id!,
            description: addCpfCnpjMask(posicao.cpfCnpj!),
          };
        });
        setInvestidoresOrigem(result);
        setInvestidorOrigemOptions(options);
      } catch {}
    },
    [dispatch, getInvestidores]
  );

  const getInvestidoresDestino = useCallback(
    async (custodianteId: string) => {
      try {
        const result = await getInvestidores(dispatch, { participanteId: custodianteId }, true);
        let filteredResult = result.filter((_) => _.id !== investidorOrigemId);

        if (tipo === TransferenciaTipo.Custodia) {
          const investidor = investidoresOrigem.find((i) => i.id === investidorOrigemId);
          filteredResult = filteredResult.filter((_) => _.cpfCnpj === investidor?.cpfCnpj);
        }

        let options = filteredResult.map((posicao) => {
          return {
            value: posicao.id,
            description: addCpfCnpjMask(posicao.cpfCnpj!),
          };
        });

        setInvestidoresDestino(result);
        setInvestidorDestinoOptions(options);
      } catch {}
    },
    [dispatch, getInvestidores, tipo, investidorOrigemId, investidoresOrigem]
  );

  const getPosicao = useCallback(
    async (investidorId: string, instrumentoFinanceiroId: string) => {
      try {
        const posicoes = await getPosicoes(dispatch, { instrumentoFinanceiroId, investidorId });
        if (posicoes.length === 0) {
          setQuantidadeMax(0);
        } else if (posicoes.length === 1) {
          const posicao = posicoes[0];
          setQuantidadeMax(posicao.saldoDisponivel);
        }
      } catch {}
    },
    [dispatch, getPosicoes]
  );

  const handleSubmit = async () => {
    if (!justificativa && attachments.length === 0) {
      dispatch(
        addAlert({ severity: 'error', message: 'É obrigatório o preenchimento do campo de Justificativa ou Anexos' })
      );
      return;
    }

    const investidorOrigem = investidoresOrigem.find((i) => i.id === investidorOrigemId);
    const investidorDestino = investidoresDestino.find((i) => i.id === investidorDestinoId);
    const custodianteOrigem = custodiantes.find((c) => c.id === custodianteOrigemId);
    const custodianteDestino = custodiantes.find((c) => c.id === custodianteDestinoId);

    if (!investidorOrigem?.cpfCnpj || !investidorDestino?.cpfCnpj || !custodianteOrigem?.cnpj || !custodianteDestino?.cnpj || !instrumento?.codigo) {
      dispatch(addAlert({ severity: 'error', message: 'Dados incompletos para a transferência.' }));
      return;
    }

    try {
      let operacaoId: string | undefined;

      if (tipo === TransferenciaTipo.Custodia) {
        const result = await POSTTransferenciaCustodiaNova(dispatch, {
          investidorOrigemCpfCnpj: investidorOrigem.cpfCnpj,
          custodianteOrigemCnpj: custodianteOrigem.cnpj,
          custodianteDestinoCnpj: custodianteDestino.cnpj,
          instrumentosFinanceiros: [{ codigo: instrumento.codigo, quantidade }],
          motivo: motivo as string,
          justificativa,
        }, true);

        operacaoId = result.itens?.[0]?.operacaoId;
      } else {
        const result = await POSTTransferenciaTitularidadeNova(dispatch, {
          investidorOrigemCpfCnpj: investidorOrigem.cpfCnpj,
          custodianteOrigemCnpj: custodianteOrigem.cnpj,
          instrumentosFinanceiros: [{
            codigo: instrumento.codigo,
            destinos: [{
              custodianteDestinoCnpj: custodianteDestino.cnpj,
              investidorDestinoCpfCnpj: investidorDestino.cpfCnpj,
              quantidade,
            }],
          }],
          motivo: motivo as string,
          justificativa,
        }, true);

        operacaoId = result.itens?.[0]?.operacaoId;
      }

      // Upload de documentos via endpoint em lote
      if (operacaoId && attachments.length > 0) {
        const itensUpload = attachments
          .filter((att) => att.file)
          .map((att) => ({ operacaoId: operacaoId!, arquivo: att.file! }));

        if (itensUpload.length > 0) {
          await POSTTransferenciaLoteComprovantes(dispatch, itensUpload, true);
        }
      }

      setFinished(true);
    } catch (error: any) {
      const responseData = error?.response?.data;
      if (responseData?.itens?.length > 0) {
        const erroMsg = responseData.itens.find((i: any) => !i.sucesso)?.mensagemErro ?? responseData.mensagem ?? 'Erro ao criar transferência.';
        dispatch(addAlert({ severity: 'error', message: erroMsg }));
      } else {
        const msg = responseData?.mensagem ?? responseData?.message ?? 'Erro ao criar transferência.';
        dispatch(addAlert({ severity: 'error', message: msg }));
      }
    }
  };

  useEffect(() => {
    getInstrumentos();
    getCustodiantes();
  }, [getInstrumentos, getCustodiantes]);

  useEffect(() => {
    setCustodianteOrigemOptions(
      custodiantes.map((custodiante) => {
        return {
          value: custodiante.id!,
          description: custodiante.razaoSocial,
        };
      })
    );

    let filteredCustodianteDestino = custodiantes;
    if (tipo === TransferenciaTipo.Custodia) {
      filteredCustodianteDestino = filteredCustodianteDestino.filter((_) => _.id !== custodianteOrigemId);
    }

    setCustodianteDestinoOptions(
      filteredCustodianteDestino.map((custodiante) => {
        return {
          value: custodiante.id!,
          description: custodiante.razaoSocial,
        };
      })
    );
  }, [custodiantes, custodianteOrigemId, tipo, investidorOrigemId]);

  useEffect(() => {
    const investidor = investidoresOrigem.find((i) => i.id === investidorOrigemId);
    if (investidor) setContaDepositoOrigem(investidor.contaDeposito);
  }, [investidorOrigemId, investidoresOrigem]);

  useEffect(() => {
    if (investidorOrigemId && instrumento) getPosicao(investidorOrigemId, instrumento.instrumentoFinanceiroGuid);
  }, [investidorOrigemId, instrumento, getPosicao]);

  useEffect(() => {
    const investidor = investidoresDestino.find((i) => i.id === investidorDestinoId);
    if (investidor) setContaDepositoDestino(investidor.contaDeposito);
  }, [investidorDestinoId, investidoresDestino]);

  useEffect(() => {
    if (custodianteOrigemId) getInvestidoresOrigem(custodianteOrigemId);
  }, [getInvestidoresOrigem, custodianteOrigemId]);

  useEffect(() => {
    if (custodianteDestinoId) getInvestidoresDestino(custodianteDestinoId);
  }, [getInvestidoresDestino, custodianteDestinoId]);

  useEffect(() => {
    if (instrumento) {
      GETInstrumentoInadimplencia(dispatch, instrumento.instrumentoFinanceiroGuid)
        .then((result) => setInstrumentoInadimplido(result?.hasPagamentosInadimplentes === true))
        .catch(() => setInstrumentoInadimplido(false));
    } else setInstrumentoInadimplido(false);
  }, [instrumento, dispatch]);

  const formFulfilled =
    tipo &&
    !!instrumento &&
    custodianteOrigemId &&
    investidorOrigemId &&
    custodianteDestinoId &&
    investidorDestinoId &&
    motivo &&
    quantidade &&
    (justificativa || attachments?.length > 0);

  return (
    <div style={{ marginRight: 40 }}>
      <Typography variant="h1">{'Nova Transferência'}</Typography>
      <LgSpace />
      {finished ? (
        <FinishedSign
          description={'Cadastro de transferência concluído com sucesso'}
          redirectRoute={'/operacoes/gerenciar'}
        />
      ) : (
        <>
          {instrumentoInadimplido && (
            <>
              <SmSpace />
              <div className={classes.errorToast}>
                <Cancel style={{ marginRight: 10 }} />
                <div style={{ display: 'flex', flex: 1 }}>
                  <Typography variant="body2">
                    {
                      'Este Instrumento está inadimplido, antes de continuar com a Movimentação solicite a ciência dos Custodiantes.'
                    }
                  </Typography>
                </div>
              </div>
            </>
          )}
          <Section title={'Detalhes da movimentação'} defaultGrid>
            <Dropdown
              label="Tipo de Transferência"
              options={transferenciaTipoOptions}
              value={tipo}
              onChange={setTipo}
              required
            />
            <InstrumentoAutocomplete instrumentos={instrumentos} instrumento={instrumento} onChange={setInstrumento} onInputChange={handleInstrumentoInputChange} loading={instrumentoSearchLoading} hasMore={hasMoreInstrumentos} onLoadMore={handleCarregarMaisInstrumentos} />
            <AutoComplete
              label="Custodiante (origem)"
              options={custodianteOrigemOptions}
              getOptionLabel={(option: Option) => String(option.description)}
              filterOptions={(options: Array<Option>, state: any) =>
                options.filter((op) =>
                  String(op.description).toUpperCase().includes(state.inputValue.toUpperCase().trim())
                )
              }
              value={custodianteOrigemOptions.find((o) => o.value === custodianteOrigemId) || null}
              onChange={(option: Option | null) => setCustodianteOrigemId(String(option?.value ?? ''))}
              required
            />
            <AutoComplete
              label="CPF/CNPJ Investidor (origem)"
              options={investidorOrigemOptions}
              getOptionLabel={(option: Option) => String(option.description)}
              filterOptions={(options: Array<Option>, state: any) =>
                options.filter((op) =>
                  String(op.description).toUpperCase().includes(state.inputValue.toUpperCase().trim())
                )
              }
              value={investidorOrigemOptions.find((o) => o.value === investidorOrigemId) || null}
              onChange={(option: Option | null) => setInvestidorOrigemId(String(option?.value ?? ''))}
              disabled={!instrumento}
              required
            />
            <TextField
              label="Conta Depósito Investidor (origem)"
              onChange={() => null}
              value={contaDepositoOrigem}
              disabled
            />
            <AutoComplete
              label="Custodiante (destino)"
              options={custodianteDestinoOptions}
              getOptionLabel={(option: Option) => String(option.description)}
              filterOptions={(options: Array<Option>, state: any) =>
                options.filter((op) =>
                  String(op.description).toUpperCase().includes(state.inputValue.toUpperCase().trim())
                )
              }
              value={custodianteDestinoOptions.find((o) => o.value === custodianteDestinoId) || null}
              onChange={(option: Option | null) => setCustodianteDestinoId(String(option?.value ?? ''))}
              required
            />
            <AutoComplete
              label="CPF/CNPJ Investidor (destino)"
              options={investidorDestinoOptions}
              getOptionLabel={(option: Option) => String(option.description)}
              filterOptions={(options: Array<Option>, state: any) =>
                options.filter((op) =>
                  String(op.description).toUpperCase().includes(state.inputValue.toUpperCase().trim())
                )
              }
              value={investidorDestinoOptions.find((o) => o.value === investidorDestinoId) || null}
              onChange={(option: Option | null) => setInvestidorDestinoId(String(option?.value ?? ''))}
              disabled={!instrumento}
              required
            />
            <TextField
              label="Conta Depósito Investidor (destino)"
              onChange={() => null}
              value={contaDepositoDestino}
              disabled
            />
            <NumberField
              label="Quantidade"
              onChange={(value) => setQuantidade(value ?? 0)}
              value={quantidade}
              numberType={getQuantityType(instrumento?.tipoInstrumentoFinanceiro)}
              helperText={`Quantidade disponível: ${formatNumber(quantidadeMax, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}`}
            />
            <Dropdown
              label="Motivo"
              options={motivoTransferenciaOptions}
              value={motivo}
              onChange={setMotivo}
              required
            />
            <TextField label="Justificativa" onChange={setJustificativa} value={justificativa} multiline rows={4} />
          </Section>
          <Section title={'Anexos'}>
            <UploadDocs
              onChange={(atts) => setAttachments(attachments.concat(atts as Attachment[]))}
              onDelete={(attId) => setAttachments(attachments.filter((att) => att.id !== attId))}
              multipleFiles={true}
            />
          </Section>
          <Grid container justify="center">
            <Grid item>
              <Button variant="contained" color="primary" disabled={!formFulfilled} onClick={handleSubmit}>
                {'Cadastrar Movimentação'}
              </Button>
              <LgSpace />
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
};

export default CadastrarOperacaoTransferencia;
